/**
 * Links in a text (1.113.0): which stretches are addresses, and what an
 * address gives away about itself before anyone opens it.
 *
 * **The check is local, and that is the decision.** A reputation service
 * (Google Safe Browsing and the like) would send every address in every post
 * to a foreign server -- a fifth exception to "no foreign services", with an
 * entry at three places (CLAUDE.md). What can be told from the address itself
 * is told here; the person decides with it in front of them, in a sheet that
 * always comes before the browser.
 *
 * **The same rule stands in PHP** (`server/src/Links.php`) for the book:
 * two languages, one rule, and `tools/pruefstand/links.ts` and
 * `tests/LinksTest.php` hold the same cases.
 */

export interface Stueck {
  art: 'text' | 'link'
  text: string
  /** For a link: the address to open (`www.` gets `https://`). */
  adresse?: string
}

/**
 * Where an address starts: `http://`, `https://` or `www.` -- at the start of
 * the text or after something that is not a letter, digit or `/`, so that a
 * word ending in `www` or a path does not start one.
 *
 * Another scheme (`javascript:`, `data:`, `file:`) never becomes a link: it
 * is not found here, and `linkPruefung()` would refuse it as well.
 */
const ANFANG = /(^|[^\p{L}\p{N}/@.])((?:https?:\/\/|www\.)[^\s<>"]+)/giu

/** What a sentence puts after an address and the address does not mean. */
const SATZZEICHEN = '.,;:!?\'"»«“”‘’…'

/**
 * The text in pieces, text and links in turn.
 *
 * **The end is trimmed**: punctuation that closes the sentence, and a closing
 * bracket without its partner inside the address -- "(siehe
 * https://example.org/a)" means `/a`, while a Wikipedia address keeps its
 * `(Begriff)`.
 */
export function findeLinks(text: string): Stueck[] {
  const stuecke: Stueck[] = []
  let bisher = 0
  for (const treffer of text.matchAll(ANFANG)) {
    const davor = treffer[1] ?? ''
    const roh = treffer[2] ?? ''
    const start = (treffer.index ?? 0) + davor.length
    const link = abschneiden(roh)
    if (link === '' || !hatHost(link)) continue
    if (start > bisher) stuecke.push({ art: 'text', text: text.slice(bisher, start) })
    stuecke.push({ art: 'link', text: link, adresse: /^www\./i.test(link) ? `https://${link}` : link })
    bisher = start + link.length
  }
  if (bisher < text.length) stuecke.push({ art: 'text', text: text.slice(bisher) })
  return stuecke
}

function abschneiden(roh: string): string {
  let link = roh
  for (;;) {
    const letztes = link.slice(-1)
    if (letztes !== '' && SATZZEICHEN.includes(letztes)) {
      link = link.slice(0, -1)
      continue
    }
    if (letztes === ')' && zaehle(link, '(') < zaehle(link, ')')) {
      link = link.slice(0, -1)
      continue
    }
    return link
  }
}

function zaehle(text: string, zeichen: string): number {
  return text.split(zeichen).length - 1
}

/** `www.` alone or `https://` alone is no address. */
function hatHost(link: string): boolean {
  const rest = link.replace(/^https?:\/\//i, '').replace(/^www\./i, '')
  return /^[^/?#]*[\p{L}\p{N}]/u.test(rest)
}

/**
 * What an address gives away about itself -- each a key, the words stand in
 * `texte/de.ts` (`links.warnung`).
 */
export type Warnung = 'unverschluesselt' | 'ip' | 'fremdeSchrift' | 'zugangsdaten' | 'port' | 'kurzlink'

/**
 * Services whose address says nothing about the target: the link leads
 * somewhere else, and where is known only after opening it. A fixed list --
 * a guess about which host forwards would be no check.
 */
export const KURZLINKS = [
  'bit.ly', 't.co', 'tinyurl.com', 'goo.gl', 'ow.ly', 'is.gd', 'buff.ly',
  'rebrand.ly', 'cutt.ly', 'shorturl.at', 'tiny.cc', 'rb.gy', 'lnkd.in', 'surl.li',
]

export interface Pruefung {
  /** The host as it stands in the address -- what the sheet shows large. */
  host: string
  /** The whole address. */
  adresse: string
  warnungen: Warnung[]
}

/**
 * The address taken apart -- or `null` when it cannot be opened at all
 * (another scheme, no host). The browser's `URL` does the parsing; the
 * host is read from the address as written, because `URL` turns a host in
 * another script into its `xn--` form and the sheet would then show a word
 * nobody typed.
 */
export function linkPruefung(adresse: string): Pruefung | null {
  let url: URL
  try {
    url = new URL(adresse)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  if (url.hostname === '') return null

  const geschrieben = /^[a-z]+:\/\/(?:[^@/?#]*@)?(\[[^\]]*\]|[^/?#:]*)/i.exec(adresse)?.[1] ?? url.hostname
  const host = geschrieben.toLowerCase()
  const warnungen: Warnung[] = []
  if (url.protocol === 'http:') warnungen.push('unverschluesselt')
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(url.hostname) || url.hostname.startsWith('[')) warnungen.push('ip')
  // A host in another script, or the `xn--` form of one: the classic
  // look-alike ("аpple.com" with a Cyrillic а).
  if (/[^\x00-\x7f]/.test(host) || /(^|\.)xn--/.test(url.hostname)) warnungen.push('fremdeSchrift')
  if (url.username !== '' || url.password !== '') warnungen.push('zugangsdaten')
  if (url.port !== '') warnungen.push('port')
  const ohneWww = url.hostname.replace(/^www\./, '')
  if (KURZLINKS.includes(ohneWww)) warnungen.push('kurzlink')
  return { host, adresse: url.href, warnungen }
}
